import { lookup } from "@repo/vihangam-yog-shared";

import { useAuth } from "../auth/AuthContext";
import { CHAPTER } from "../chapter";
import { friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

import { formatMoney } from "./event-registrations/shared";

/**
 * Member landing page. Approved members see their own card — nothing
 * else in the team app is reachable for them. Only chapters that run
 * memberships route here.
 *
 * Reads from memberSelf so the holder sees the full record on file:
 * membership type, status, payment-received-at, family members, etc.
 */

/** Membership data we don't have. */
const NOT_RECORDED = "Not recorded";

const STATUS_LABEL = {
  active: "Active",
  pending: "Waiting for payment",
  cancelled: "Cancelled",
} satisfies Record<string, string>;

const MEMBERSHIP_TYPE_LABEL = {
  individual: "Individual",
  family: "Family",
} satisfies Record<string, string>;

/** Stored payment methods in plain words; anything typed by hand shows
 *  as written. */
const PAYMENT_METHOD_LABEL = {
  bank_transfer: "Bank transfer",
  cash: "Cash",
  card: "Card",
  stripe: "Card",
} satisfies Record<string, string>;

const buttonClass =
  "inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-5 text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]";

export function MemberCard() {
  const { user, signOut } = useAuth();
  const query = trpc.memberSelf.useQuery(undefined, { retry: false });
  const firstName = user?.fullName?.trim().split(/\s+/)[0] ?? "";

  const member = query.data;
  const inbox = (
    <a
      href={`mailto:${CHAPTER.contactInbox}`}
      className="break-all text-[color:var(--color-amber)] underline underline-offset-4"
    >
      {CHAPTER.contactInbox}
    </a>
  );

  return (
    <main className="min-h-screen bg-[color:var(--color-bg)] px-5 py-10 md:py-20">
      <div className="mx-auto max-w-3xl">
        <p className="text-[15px] text-[color:var(--color-ink-soft)]">
          {CHAPTER.name} membership
        </p>
        <h1 className="page-title mt-3">
          {firstName ? `Welcome, ${firstName}` : "Welcome"}
        </h1>
        <p className="mt-2 text-[20px] leading-snug text-[color:var(--color-amber)]">
          You&apos;re a member.
        </p>

        <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
          This is your membership card. To change anything, email {inbox} and an
          organiser will update it for you.
        </p>

        {query.isLoading ? (
          <p
            role="status"
            className="mt-10 text-[16px] text-[color:var(--color-ink-soft)]"
          >
            Loading your membership…
          </p>
        ) : query.isError ? (
          <p
            role="alert"
            className="mt-10 text-[16px] text-[color:var(--color-ink-soft)]"
          >
            {friendlyError(
              query.error,
              "We couldn't load your membership. Check your internet connection and reload the page.",
            )}
          </p>
        ) : !member ? (
          <p className="mt-10 text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
            We couldn&apos;t find your membership details. Try signing out and
            signing back in. If that doesn&apos;t help, email {inbox}.
          </p>
        ) : (
          <div className="surface mt-8 p-5 md:p-7">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-[18px] font-semibold text-[color:var(--color-ink)]">
                Your membership
              </h2>
              <StatusBadge status={member.status} />
            </div>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
              <Cell label="Name" value={member.fullName} />
              <Cell
                label="Email"
                value={member.email ?? user?.email ?? NOT_RECORDED}
              />
              <Cell
                label="Membership"
                value={
                  lookup(MEMBERSHIP_TYPE_LABEL, member.membershipType) ??
                  member.membershipType
                }
              />
              <Cell
                label="Member since"
                value={
                  member.paymentReceivedAt
                    ? formatDate(member.paymentReceivedAt)
                    : member.status === "active"
                      ? formatDate(member.createdAt)
                      : NOT_RECORDED
                }
              />
              <Cell label="Phone" value={member.contactNo ?? NOT_RECORDED} />
              <Cell
                label="Address"
                value={member.address ?? NOT_RECORDED}
                spanAll
              />
              {member.amountPaidCents !== null ? (
                <Cell
                  label="Payment on file"
                  value={
                    member.paymentMethod
                      ? `${formatMoney(member.amountPaidCents)} · ${
                          lookup(PAYMENT_METHOD_LABEL, member.paymentMethod) ??
                          member.paymentMethod
                        }`
                      : formatMoney(member.amountPaidCents)
                  }
                />
              ) : null}
            </dl>

            {member.familyMembers.length > 0 ? (
              <div className="mt-7 border-t border-[color:var(--color-line)] pt-5">
                <h3 className="text-[14px] font-semibold text-[color:var(--color-ink-soft)]">
                  Family members
                </h3>
                <ul className="mt-3 flex flex-col gap-2">
                  {member.familyMembers.map((m, i) => (
                    <li
                      key={`${m.name}-${i}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-bg)] px-3 py-2.5"
                    >
                      <span className="text-[16px] break-words text-[color:var(--color-ink)]">
                        {m.name}
                      </span>
                      {m.gender ? (
                        <span className="text-[14px] text-[color:var(--color-ink-soft)]">
                          {m.gender === "M" ? "Male" : "Female"}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {member.notes ? (
              <div className="mt-7 border-t border-[color:var(--color-line)] pt-5">
                <h3 className="text-[14px] font-semibold text-[color:var(--color-ink-soft)]">
                  Notes
                </h3>
                <p className="mt-2 text-[16px] leading-relaxed break-words text-[color:var(--color-ink)]">
                  {member.notes}
                </p>
              </div>
            ) : null}
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a href={`mailto:${CHAPTER.contactInbox}`} className={buttonClass}>
            Email us to update my details
          </a>
          <button
            type="button"
            onClick={() => {
              void signOut();
            }}
            className={buttonClass}
          >
            Sign out
          </button>
        </div>
      </div>
    </main>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "active"
      ? { bg: "var(--color-amber)", color: "var(--color-paper)" }
      : status === "pending"
        ? {
            bg: "color-mix(in srgb, var(--color-amber) 20%, transparent)",
            color: "var(--color-amber)",
          }
        : {
            bg: "color-mix(in srgb, var(--color-ink) 12%, transparent)",
            color: "var(--color-ink-soft)",
          };
  return (
    <span className="badge" style={{ background: tone.bg, color: tone.color }}>
      {lookup(STATUS_LABEL, status) ?? status}
    </span>
  );
}

function Cell({
  label,
  value,
  spanAll,
}: {
  label: string;
  value: string;
  spanAll?: boolean;
}) {
  return (
    <div className={spanAll ? "sm:col-span-2" : undefined}>
      <dt className="text-[14px] font-medium text-[color:var(--color-ink-soft)]">
        {label}
      </dt>
      <dd className="mt-0.5 text-[16px] break-words text-[color:var(--color-ink)]">
        {value}
      </dd>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
